import React from "react";

import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
  PaymentMethodForm,
  useSetupIntentReturn,
} from "@/components/billing/payment-method-form";
import { PaymentMethodSummary } from "@/components/billing/payment-method-summary";
import Button from "@/components/button";
import { LanguageToggle } from "@/components/language-toggle";
import { useAuth } from "@/context/auth";
import {
  billingStatusQueryOptions,
  useBillingPortalMutation,
  useBillingStatusQuery,
  useDismissPastDueMutation,
  useResumeBillingMutation,
  useRetryBillingPaymentMutation,
} from "@/queries/hooks/billingHooks";
import type { BillingStatus } from "@/schemas/billing";
import { isApiError } from "@/queries/api/apiUtils";
import { BILLING_RESUME_ERROR_CODES } from "@/schemas/billing";
import { notify } from "@/utils";
import {
  SUBSCRIPTION_BILLING_ENABLED,
  formatBillingDate,
  formatCents,
  isBillingVisible,
  isSubscriptionEnded,
  resolveVariant,
  tierLabel,
  markBillingResolveDismissed,
} from "@/utils/billing";

/**
 * Payment-problem resolution page (DESIGN §3.4). Deliberately outside the
 * `_app` shell: while the account is blocked there is no app navigation —
 * only fixing the payment or logging out.
 */
export const Route = createFileRoute("/app/$role/billing/resolve")({
  beforeLoad: async ({ context, params }) => {
    const user = context.auth?.user;
    if (!SUBSCRIPTION_BILLING_ENABLED || user?.role !== "employer") {
      throw redirect({ to: "/app/$role/dashboard", params });
    }
    let status: BillingStatus | undefined;
    try {
      status = await context.queryClient.ensureQueryData({
        ...billingStatusQueryOptions(user.userId),
        retry: false,
      });
    } catch {
      // Unknown — render the page's own load-error / retry state rather than
      // bouncing (a 402 elsewhere may be what sent the owner here).
      return;
    }
    if (
      !isBillingVisible({
        flagEnabled: SUBSCRIPTION_BILLING_ENABLED,
        role: user.role,
        status,
      })
    ) {
      // Account not opted in to billing — nothing to resolve.
      throw redirect({ to: "/app/$role/dashboard", params });
    }
  },
  component: BillingResolvePage,
});

function isResolved(s: BillingStatus): boolean {
  return resolveVariant(s) === "resolved";
}

function BillingResolvePage() {
  const { t, i18n } = useTranslation("billing");
  const auth = useAuth();
  const router = useRouter();
  const navigate = useNavigate();
  const role = auth.user?.role ?? "employer";
  const statusQuery = useBillingStatusQuery();
  const status = statusQuery.data;
  const [updating, setUpdating] = React.useState(false);

  const retry = useRetryBillingPaymentMutation();
  const dismiss = useDismissPastDueMutation();
  const portal = useBillingPortalMutation();

  const goToDashboard = React.useCallback(() => {
    void navigate({
      to: "/app/$role/dashboard",
      params: { role },
      replace: true,
    });
  }, [navigate, role]);

  const afterUpdate = React.useCallback(
    (s: BillingStatus) => {
      setUpdating(false);
      if (isResolved(s)) {
        notify.success(t("resolve.resolved"));
        goToDashboard();
      } else {
        notify.error(t("resolve.stillFailing"));
      }
    },
    [goToDashboard, t],
  );

  const { pending: returnPending } = useSetupIntentReturn({
    onConfirmed: afterUpdate,
  });

  async function handleLogout() {
    await auth.logout();
    await router.invalidate();
    await navigate({ to: "/" });
  }

  async function handleRetry() {
    try {
      afterUpdate(await retry.mutateAsync());
    } catch {
      // toasted by the global mutation handler
    }
  }

  async function handleDismiss() {
    try {
      await dismiss.mutateAsync();
      markBillingResolveDismissed(auth.user?.userId);
      goToDashboard();
    } catch {
      // 409 (no dismissals left) etc. — toasted globally; status refetched.
    }
  }

  const header = (
    <header className="px-4 md:px-12 py-8 flex items-center justify-between gap-3">
      <img
        src="/newlogo.svg"
        alt="Public Market"
        height={40}
        className="h-10 w-auto"
      />
      <div className="flex items-center gap-3">
        <LanguageToggle variant="compact" />
        <Button type="button" variant="outline" size="sm" onClick={handleLogout}>
          {t("resolve.logout")}
        </Button>
      </div>
    </header>
  );

  let body: React.ReactNode;
  if (statusQuery.isLoading || returnPending) {
    body = <p className="body-small text-gray-500">{t("form.loading")}</p>;
  } else if (!status) {
    body = (
      <div className="flex flex-col gap-4 items-start">
        <p className="body-small text-red-600" role="alert">
          {t("resolve.loadFailed")}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => statusQuery.refetch()}
        >
          {t("form.tryAgain")}
        </Button>
      </div>
    );
  } else if (resolveVariant(status) === "resolved") {
    body = (
      <div className="flex flex-col gap-4 items-start">
        <h1 className="heading-1">{t("resolve.allSetTitle")}</h1>
        <p className="body-small text-gray-600">{t("resolve.allSetBody")}</p>
        <Button type="button" variant="primary" onClick={goToDashboard}>
          {t("resolve.goToDashboard")}
        </Button>
      </div>
    );
  } else if (resolveVariant(status) === "voluntaryEnded") {
    body = (
      <VoluntaryEndedBody
        status={status}
        onResolved={(s) => {
          if (isResolved(s)) {
            notify.success(t("resume.resumed"));
            goToDashboard();
          } else {
            notify.error(t("resolve.stillFailing"));
          }
        }}
        onManageBilling={() => portal.mutate(window.location.href)}
        portalPending={portal.isPending}
      />
    );
  } else {
    const canceled = isSubscriptionEnded(status.status);
    const invoice = status.openInvoice;
    const nextAttempt = formatBillingDate(invoice?.nextAttemptAt);
    const canDismiss = !status.blocked && status.dismissalsRemaining > 0;
    const busy = retry.isPending || dismiss.isPending || portal.isPending;

    body = (
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="heading-1">
            {canceled ? t("resolve.canceledTitle") : t("resolve.title")}
          </h1>
          <p className="body-small text-gray-600 mt-2">
            {canceled ? t("resolve.canceledBody") : t("resolve.body")}
          </p>
          {status.blocked ? (
            <p className="body-small font-semibold text-garnet mt-2">
              {t("resolve.blockedNotice")}
            </p>
          ) : null}
        </div>

        {invoice ? (
          <div className="rounded-md ring-1 ring-garnet/30 bg-fog/40 p-4 flex flex-col gap-1 max-w-[520px]">
            <div className="body-small text-gray-500">
              {t("resolve.amountDue")}
            </div>
            <div className="text-xl font-semibold text-garnet">
              {formatCents(invoice.amountDueCents, invoice.currency, i18n.language)}
            </div>
            {invoice.failureMessage ? (
              <p className="body-small text-gray-700">
                {t("resolve.reason", { reason: invoice.failureMessage })}
              </p>
            ) : null}
            {nextAttempt && !status.blocked ? (
              <p className="body-small text-gray-600">
                {t("resolve.nextAttempt", { date: nextAttempt })}
              </p>
            ) : null}
            {invoice.hostedInvoiceUrl ? (
              <a
                href={invoice.hostedInvoiceUrl}
                target="_blank"
                rel="noreferrer"
                className="body-small"
              >
                {t("resolve.viewInvoice")}
              </a>
            ) : null}
          </div>
        ) : null}

        {status.paymentMethod && !updating ? (
          <PaymentMethodSummary
            paymentMethod={status.paymentMethod}
            pending={status.paymentMethodPending}
          />
        ) : null}

        {updating ? (
          <div className="max-w-[520px]">
            <PaymentMethodForm
              returnUrl={window.location.origin + window.location.pathname}
              submitLabel={
                invoice ? t("resolve.saveAndRetry") : t("form.save")
              }
              onSaved={afterUpdate}
              onCancel={() => setUpdating(false)}
            />
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <Button
              type="button"
              variant="primary"
              onClick={() => setUpdating(true)}
              disabled={busy}
            >
              {t("resolve.updatePaymentMethod")}
            </Button>
            {invoice && !canceled ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleRetry}
                disabled={busy}
              >
                {retry.isPending ? t("resolve.retrying") : t("resolve.retry")}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              onClick={() => portal.mutate(window.location.href)}
              disabled={busy}
            >
              {t("manageBilling")}
            </Button>
          </div>
        )}

        {canDismiss && !updating ? (
          <div>
            <Button
              type="button"
              variant="link"
              onClick={handleDismiss}
              disabled={busy}
            >
              {t("resolve.remindLater", { count: status.dismissalsRemaining })}
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {header}
      <main className="w-full px-4 md:px-12 pb-16">
        <div className="max-w-[720px] mx-auto pt-6">{body}</div>
      </main>
    </div>
  );
}

/**
 * The owner canceled (Stripe portal, at period end) and the period is over.
 * Resuming starts a new subscription right away — no trial, charged today.
 */
function VoluntaryEndedBody({
  status,
  onResolved,
  onManageBilling,
  portalPending,
}: {
  status: BillingStatus;
  onResolved: (s: BillingStatus) => void;
  onManageBilling: () => void;
  portalPending: boolean;
}) {
  const { t, i18n } = useTranslation("billing");
  const resume = useResumeBillingMutation();
  const [useDifferent, setUseDifferent] = React.useState(false);
  const [declined, setDeclined] = React.useState<string | null>(null);

  const price =
    status.priceAmountCents !== null
      ? formatCents(status.priceAmountCents, status.currency, i18n.language)
      : null;
  const tier = status.tier ? tierLabel(status.tier) : null;
  const hasMethod = Boolean(status.paymentMethod);
  const showForm = !hasMethod || useDifferent;

  async function handleResume() {
    setDeclined(null);
    try {
      onResolved(await resume.mutateAsync());
    } catch (e) {
      if (
        isApiError(e) &&
        e.code === BILLING_RESUME_ERROR_CODES.paymentMethodRequired
      ) {
        setUseDifferent(true);
        notify.error(t("resume.paymentMethodRequired"));
      } else if (
        isApiError(e) &&
        e.code === BILLING_RESUME_ERROR_CODES.paymentFailed
      ) {
        // Declined: show why and offer another payment method. Also toast —
        // the refetched status ("incomplete") may switch the page to the
        // payment-problem variant, unmounting this inline message.
        const msg = e.message || t("resume.declined");
        setDeclined(msg);
        notify.error(msg);
      } else {
        notify.error(
          e instanceof Error && e.message ? e.message : t("resume.failed"),
        );
      }
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="heading-1">{t("resume.endedTitle")}</h1>
        <p className="body-small text-gray-600 mt-2">{t("resume.endedBody")}</p>
      </div>

      {tier || price ? (
        <div className="rounded-md ring-1 ring-garnet/30 bg-fog/40 p-4 flex flex-col gap-1 max-w-[520px]">
          <div className="body-small text-gray-500">
            {t("settings.planLabel")}
          </div>
          <div className="flex flex-row flex-wrap items-baseline gap-x-3">
            {tier ? (
              <span className="text-lg font-semibold text-garnet">
                {t(tier.key, tier.params)}
              </span>
            ) : null}
            {price ? (
              <span className="body-small text-garnet/80">
                {t("settings.pricePerMonth", { price })}
              </span>
            ) : null}
          </div>
          {price ? (
            <p className="body-small text-gray-700">
              {t("resume.chargedToday", { price })}
            </p>
          ) : null}
        </div>
      ) : null}

      {declined ? (
        <p className="body-small text-red-700" role="alert">
          {declined}
        </p>
      ) : null}

      {showForm ? (
        <div className="max-w-[520px]">
          <PaymentMethodForm
            returnUrl={window.location.origin + window.location.pathname}
            submitLabel={t("resume.button")}
            onSaved={onResolved}
            onCancel={hasMethod ? () => setUseDifferent(false) : undefined}
          />
        </div>
      ) : (
        <>
          {status.paymentMethod ? (
            <PaymentMethodSummary
              paymentMethod={status.paymentMethod}
              pending={status.paymentMethodPending}
            />
          ) : null}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <Button
              type="button"
              variant="primary"
              onClick={handleResume}
              disabled={resume.isPending}
            >
              {resume.isPending ? t("resume.resuming") : t("resume.button")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeclined(null);
                setUseDifferent(true);
              }}
              disabled={resume.isPending}
            >
              {t("resume.useDifferent")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onManageBilling}
              disabled={resume.isPending || portalPending}
            >
              {t("manageBilling")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
